import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { authenticationService } from "../../api/authentication.service";
import MainStructure from "../containers/MainStructure";
import AuthModal from "../NewPedigree/AuthModal";
import useWebReportMeta from "../../hooks/useWebReportMeta";

const ReportAccessContext = createContext(() => {});
export const useReportAccess = () => useContext(ReportAccessContext);

export default function ReportAccessGate({ children, collection = false }) {
  const [session, setSession] = useState(
    authenticationService.currentUserValue,
  );
  const [needsLogin, setNeedsLogin] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const [tab, setTab] = useState("login");
  useEffect(() => {
    const subscription = authenticationService.currentUser.subscribe(
      (nextSession) => {
        setSession(nextSession);
        if (!nextSession) setModalOpen(true);
      },
    );
    return () => subscription.unsubscribe();
  }, []);
  const requireAuthentication = useCallback(() => {
    setNeedsLogin(true);
    setModalOpen(true);
  }, []);
  function openAuthentication(nextTab = "login") {
    setTab(nextTab);
    setModalOpen(true);
  }
  if (session && !needsLogin)
    return (
      <ReportAccessContext.Provider value={requireAuthentication}>
        {children}
      </ReportAccessContext.Provider>
    );
  return (
    <MainStructure showSidebar={false} onAuthenticate={openAuthentication}>
      <LockedReport
        collection={collection}
        onAuthenticate={openAuthentication}
      />
      <AuthModal
        key={tab}
        initialTab={tab}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        purpose="report"
        onAuthenticated={(authenticatedSession) => {
          setSession(authenticatedSession);
          setNeedsLogin(false);
          setModalOpen(false);
        }}
      />
    </MainStructure>
  );
}

function LockedReport({ collection, onAuthenticate }) {
  useWebReportMeta(
    collection ? "Shared horse comparison" : "Shared pedigree report",
  );
  return (
    <main className="web-report report-access">
      <header className="report-hero">
        <div>
          <p className="report-eyebrow">ChampionBred pedigree intelligence</p>
          <h1>
            {collection ? "A collection to explore" : "A report to explore"}
          </h1>
          <p>
            Log in or create an account to view this shared{" "}
            {collection ? "collection" : "report"}.
          </p>
        </div>
        <button onClick={() => onAuthenticate("login")}>
          Unlock {collection ? "collection" : "report"}
        </button>
      </header>
      <div
        className="report-access__preview"
        aria-hidden="true"
        data-testid="locked-report-preview"
      >
        <div className="report-access__line report-access__line--title" />
        <div className="report-access__stats">
          {Array.from({ length: collection ? 4 : 2 }, (_, index) => (
            <div className="report-panel" key={index}>
              <div className="report-access__line" />
              <div className="report-access__line report-access__line--score" />
              <div className="report-access__line" />
            </div>
          ))}
        </div>
        <div className="report-panel">
          <div className="report-access__line report-access__line--title" />
          <div className="report-access__chart">
            {[35, 60, 45, 85, 70, 50, 30].map((height, index) => (
              <div key={index} style={{ height: `${height}%` }} />
            ))}
          </div>
        </div>
        <div className="report-panel report-access__rows">
          {Array.from({ length: 5 }, (_, index) => (
            <div className="report-access__line" key={index} />
          ))}
        </div>
      </div>
      <p className="report-note">
        Report results are available after you sign in.
      </p>
    </main>
  );
}
